import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg, useApi } from "@/lib/api";

export const MAX_BYTES = 5 * 1024 * 1024;
const TYPES = ["image/png", "image/jpeg", "image/webp"];

// Client-side check (translated); the server re-validates and re-encodes every image.
export function checkFiles(files, already, max, t) {
  if (already + files.length > max) return t("dialog.err_count", { n: max });
  for (const f of files) {
    if (!TYPES.includes(f.type)) return t("dialog.err_type");
    if (f.size > MAX_BYTES) return t("dialog.err_size");
  }
  return null;
}

export function useCategories(lang) {
  const api = useApi();
  const [c, setC] = useState({});
  useEffect(() => {
    let live = true;
    api.get("/help/categories", { params: { lang } }).then(({ data }) => live && setC(data)).catch(() => live && setC({}));
    return () => { live = false; };
  }, [api, lang]);
  return c;
}

export function uploadFile(api, tid, rid, file) {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/t/${tid}/support-requests/${rid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
}

// Attachments are served only through the tenant-scoped API (auth header), never as public URLs.
function Thumb({ tid, rid, a }) {
  const api = useApi();
  const { t } = useTranslation("help");
  const [url, setUrl] = useState(null);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    let u = null;
    api.get(`/t/${tid}/support-requests/${rid}/attachments/${a.id}`, { responseType: "blob" }).then(({ data }) => { u = URL.createObjectURL(data); setUrl(u); }).catch(() => setBad(true));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [api, tid, rid, a.id]);
  if (bad) return <span className="flex h-20 w-28 items-center justify-center rounded border border-slate-200 text-[11px] text-slate-500">{t("req.image_error")}</span>;
  if (!url) return <span className="flex h-20 w-28 items-center justify-center rounded border border-slate-200"><Loader2 size={14} className="animate-spin text-slate-400" /></span>;
  return <a href={url} target="_blank" rel="noreferrer" data-testid={`support-att-${a.id}`}><img src={url} alt={a.name} className="h-20 w-28 rounded border border-slate-200 object-cover" /></a>;
}

export function Attachments({ tid, r, max, canAdd, onChange }) {
  const api = useApi();
  const { t } = useTranslation("help");
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    const files = [...e.target.files]; e.target.value = "";
    const bad = checkFiles(files, r.attachments.length, max, t);
    if (bad) return toast.error(bad);
    setBusy(true);
    try { for (const f of files) await uploadFile(api, tid, r.id, f); toast.success(t("req.attach_added")); onChange(); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  if (!r.attachments.length && !canAdd) return null;
  return (
    <div className="space-y-1.5" data-testid={`support-attachments-${r.id}`}>
      {r.attachments.length > 0 && <p className="text-[11px] font-medium text-slate-600">{t("req.attachments")}</p>}
      <div className="flex flex-wrap gap-2">{r.attachments.map((a) => <Thumb key={a.id} tid={tid} rid={r.id} a={a} />)}</div>
      {canAdd && r.attachments.length < max && <label className="inline-flex min-h-[40px] cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 px-3 text-xs text-slate-700 hover:bg-slate-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}{t("req.attach_more")}
        <input type="file" accept="image/png,image/jpeg,image/webp" multiple className="sr-only" onChange={add} disabled={busy} data-testid={`support-att-add-${r.id}`} /></label>}
    </div>);
}
